import React, { useEffect, useState } from "react";
import { Box, Paper, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import StudentIdCardForm from "./StudentIdCardForm";
import StudentIdCardTable from "./StudentIdCardTable";
import { idCardApi } from "./idCardApi";
import StudentIdCardPreview from "./StudentIdCardPreview";

const StudentIdCardPage: React.FC = () => {
  const academicYearMarch31 = () => {
    const now = new Date();
    const year = now.getMonth() >= 3 ? now.getFullYear() + 1 : now.getFullYear();
    return new Date(year, 2, 31).toISOString().substring(0, 10);
  };
  const [form, setForm] = useState<{ mode: "AUTO" | "MANUAL"; studentId?: string; photoUrl?: string; studentName?: string; scholarNo?: string; className?: string; sectionName?: string; dob?: string; bloodGroup?: string; validUpto?: string }>({
    mode: "AUTO",
    validUpto: academicYearMarch31(),
  });
  const [rows, setRows] = useState<any[]>([]);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" | "info" }>({ open: false, message: "", severity: "info" });
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);

  const load = async () => {
    try {
      const res = await idCardApi.list({});
      console.log("IDCard: list response", res);
      setRows(res);
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || "Failed to load ID cards", severity: "error" });
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    try {
      console.log("IDCard: save payload", form);
      await idCardApi.create(form);
      setSnackbar({ open: true, message: "ID Card saved", severity: "success" });
      setForm({ mode: "AUTO" });
      load();
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || "Failed to save", severity: "error" });
    }
  };

  const edit = async (row: any) => {
    setForm({
      mode: "MANUAL",
      studentId: row.studentId || undefined,
      photoUrl: row.photoUrl || undefined,
      studentName: row.studentName,
      scholarNo: row.scholarNo,
      className: row.className,
      sectionName: row.sectionName,
      dob: row.dob ? new Date(row.dob).toISOString().substring(0, 10) : undefined,
      bloodGroup: row.bloodGroup || undefined,
      validUpto: row.validUpto ? new Date(row.validUpto).toISOString().substring(0, 10) : undefined,
    });
  };

  const remove = async (row: any) => {
    try {
      await idCardApi.delete(row.id);
      setSnackbar({ open: true, message: "Deleted", severity: "success" });
      load();
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || "Failed to delete", severity: "error" });
    }
  };

  const exportExcel = async () => {
    try {
      const res = await idCardApi.export({});
      const blob = res.data as Blob;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "id-cards.xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || "Failed to export", severity: "error" });
    }
  };

  const print = (row: any) => {
    idCardApi.pdf(row.id).then((res) => {
      const blob = res.data as Blob;
      const url = window.URL.createObjectURL(blob);
      let filename = "id-card.pdf";
      const cd = res.headers?.["content-disposition"] || res.headers?.["Content-Disposition"];
      if (cd) {
        const m = cd.match(/filename=\"?([^\";]+)\"?/i);
        if (m && m[1]) filename = m[1];
      }
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }).catch((e: any) => {
      setSnackbar({ open: true, message: e.response?.data?.message || "Failed to print", severity: "error" });
    });
  };

  const preview = async (row: any) => {
    try {
      const res = await idCardApi.preview(row.id);
      setPreviewHtml(res.html);
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || "Failed to load preview", severity: "error" });
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ fontSize: 22, fontWeight: 600, mb: 2 }}>Student ID Card</Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <StudentIdCardForm form={form} onChange={setForm} onSubmit={save} />
      </Paper>
      <Paper sx={{ p: 2 }}>
        <StudentIdCardTable rows={rows} onEdit={edit} onDelete={remove} onExport={exportExcel} onPrint={print} onPreview={preview} />
      </Paper>
      <Dialog open={!!previewHtml} onClose={() => setPreviewHtml(null)} maxWidth="md" fullWidth>
        <DialogTitle>ID Card Preview</DialogTitle>
        <DialogContent dividers>
          {previewHtml && <StudentIdCardPreview html={previewHtml} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewHtml(null)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar((p) => ({ ...p, open: false }))}>
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StudentIdCardPage;
